import {
  projectDailyCare,
  type DailyCareInput,
  type DailyCareOutcome,
  type DailyCareSource,
  type DailyCareSourceProjection,
  type DailyCareSourceRow,
} from './daily-care';

const sources = ['roster', 'services', 'rounds', 'adl', 'tasks', 'woundTasks'] as const;
type ControlledSource = (typeof sources)[number];
type Rpc = (source: ControlledSource, cursor: string | null) => Promise<unknown>;
const outcomes = new Set([
  'scheduled',
  'overdue',
  'in_progress',
  'recorded',
  'delivered',
  'refused',
  'omitted',
  'unknown',
]);
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Traverse only the scoped controlled reader; a malformed or failed page is never an empty result. */
export async function readDailyCareSources(input: {
  clientId: string;
  contextRevision: string;
  rpc: Rpc;
  capacity?: number;
}) {
  const projections = {} as Record<ControlledSource, DailyCareSourceProjection>;
  const cap = input.capacity ?? 2000;
  if (!Number.isInteger(cap) || cap < 1) throw new Error('daily_care_capacity_invalid');
  for (const source of sources) {
    const rows: DailyCareSourceRow[] = [];
    let cursor: string | null = null;
    let coverage: DailyCareSourceProjection['coverage'] = 'ready';
    try {
      for (;;) {
        const response = await input.rpc(source, cursor);
        if (!response || typeof response !== 'object' || Array.isArray(response))
          throw new Error('invalid_page');
        const page = response as Record<string, unknown>;
        if (
          !Array.isArray(page.rows) ||
          typeof page.hasMore !== 'boolean' ||
          !['ready', 'limited', 'not-applicable', 'not-permitted'].includes(String(page.coverage))
        )
          throw new Error('invalid_page');
        if (
          (page.coverage === 'not-applicable' || page.coverage === 'not-permitted') &&
          (page.rows.length || page.hasMore)
        )
          throw new Error('invalid_page');
        if (page.coverage === 'limited') coverage = 'limited';
        if (page.coverage === 'not-applicable' || page.coverage === 'not-permitted')
          coverage = page.coverage;
        let last: string | null = cursor;
        let truncated = false;
        for (const value of page.rows) {
          if (!value || typeof value !== 'object' || Array.isArray(value))
            throw new Error('invalid_row');
          const r = value as Record<string, unknown>;
          if (
            typeof r.id !== 'string' ||
            !uuid.test(r.id) ||
            (last !== null && r.id <= last) ||
            typeof r.label !== 'string' ||
            (r.at !== null && (typeof r.at !== 'string' || !Number.isFinite(Date.parse(r.at)))) ||
            typeof r.outcome !== 'string' ||
            !outcomes.has(r.outcome)
          )
            throw new Error('invalid_row');
          if (
            r.priority != null &&
            (typeof r.priority !== 'string' ||
              !['URGENT', 'HIGH', 'MEDIUM', 'LOW'].includes(r.priority))
          )
            throw new Error('invalid_recorded_priority');
          for (const key of ['roster_shift_id', 'round_assignment_id'])
            if (r[key] != null && (typeof r[key] !== 'string' || !uuid.test(r[key] as string)))
              throw new Error('invalid_row');
          last = r.id;
          if (rows.length >= cap) {
            coverage = 'limited';
            truncated = true;
            break;
          }
          rows.push({
            id: r.id,
            clientId: input.clientId,
            contextRevision: input.contextRevision,
            label: r.label,
            href:
              source === 'tasks' || source === 'woundTasks'
                ? `/tasks/${r.id}`
                : source === 'roster'
                  ? `/roster?shift=${r.id}`
                  : source === 'services'
                    ? r.roster_shift_id
                      ? `/roster?shift=${r.roster_shift_id}`
                      : '/roster'
                    : source === 'rounds'
                      ? `/residential/rounds/${r.id}`
                      : `/residential/adl-events/${r.id}`,
            at: r.at,
            outcome: r.outcome as DailyCareSourceRow['outcome'],
            priority: (r.priority ?? null) as DailyCareSourceRow['priority'],
            rosterShiftId: r.roster_shift_id as string | null,
            roundAssignmentId: r.round_assignment_id as string | null,
          });
        }
        if (rows.length >= cap && (page.hasMore || truncated)) {
          coverage = 'limited';
          break;
        }
        if (!page.hasMore) {
          if (page.nextCursor != null) throw new Error('invalid_cursor');
          break;
        }
        if (!last || page.nextCursor !== last || page.rows.length === 0)
          throw new Error('invalid_cursor');
        if (rows.length >= cap) {
          coverage = 'limited';
          break;
        }
        cursor = last;
      }
      projections[source] = { coverage, rows };
    } catch {
      projections[source] = { coverage: 'unavailable', rows: [] };
    }
  }
  return projections;
}
export function dailyCareWithSources(
  clientId: string,
  contextRevision: string,
  sourcesRead: Record<ControlledSource, DailyCareSourceProjection>,
  mar?: DailyCareSourceProjection,
  medicationSchedule?: DailyCareSourceProjection,
) {
  const sources = {
    ...sourcesRead,
    medicationSchedule: medicationSchedule ?? { coverage: 'unavailable', rows: [] },
    mar: mar ?? { coverage: 'unavailable', rows: [] },
  } as Record<DailyCareSource, DailyCareSourceProjection>;
  return projectDailyCare({ clientId, contextRevision, sources } as DailyCareInput);
}

/** The established MAR window reader supplies only confirmed recorded outcomes. */
export async function readDailyCareMar(input: {
  clientId: string;
  contextRevision: string;
  read: () => Promise<unknown>;
}): Promise<DailyCareSourceProjection> {
  try {
    const result = await input.read();
    if (!result || typeof result !== 'object' || Array.isArray(result))
      throw new Error('invalid_mar');
    const envelope = result as Record<string, unknown>;
    if (envelope.complete !== true || !Array.isArray(envelope.rows))
      throw new Error('incomplete_mar');
    const seen = new Set<string>();
    const rows: DailyCareSourceRow[] = envelope.rows.map((value) => {
      if (!value || typeof value !== 'object' || Array.isArray(value))
        throw new Error('invalid_mar_row');
      const row = value as Record<string, unknown>;
      if (
        typeof row.id !== 'string' ||
        !uuid.test(row.id) ||
        seen.has(row.id) ||
        row.client_id !== input.clientId ||
        typeof row.status !== 'string'
      )
        throw new Error('invalid_mar_row');
      seen.add(row.id);
      const at = row.administered_at ?? row.scheduled_time;
      if (typeof at !== 'string' || !Number.isFinite(Date.parse(at)))
        throw new Error('invalid_mar_row');
      const status = row.status.toUpperCase();
      // Preserve the recorded reason; a withheld or unavailable dose is not a missed dose.
      const medicationOutcomes: Record<string, DailyCareOutcome> = {
        REFUSED: 'refused',
        MISSED: 'missed',
        WITHHELD: 'withheld',
        NOT_AVAILABLE: 'not_available',
        ADMINISTERED: 'recorded',
        SELF_ADMINISTERED: 'self_administered',
      };
      const outcome = medicationOutcomes[status] ?? 'unknown';
      return {
        id: row.id,
        clientId: input.clientId,
        contextRevision: input.contextRevision,
        label: 'Medication administration',
        href: `/clients/${encodeURIComponent(input.clientId)}/medications`,
        at,
        outcome,
      };
    });
    return { coverage: 'ready', rows };
  } catch {
    return { coverage: 'unavailable', rows: [] };
  }
}

/** Only confirmed prescription occurrences returned by the existing timing reader are admitted. */
export async function readDailyCareMedicationSchedule(input: {
  clientId: string;
  contextRevision: string;
  medicationIds: string[];
  read: (id: string) => Promise<unknown>;
}): Promise<DailyCareSourceProjection> {
  const rows: DailyCareSourceRow[] = [];
  let coverage: DailyCareSourceProjection['coverage'] = 'limited';
  const ids = [...new Set(input.medicationIds)];
  try {
    for (const id of ids.slice(0, 100)) {
      const result = await input.read(id);
      if (!result || typeof result !== 'object' || Array.isArray(result))
        throw new Error('invalid_timing');
      const value = result as Record<string, unknown>;
      if (value.confirmed !== true) continue;
      const projection = value.projection as Record<string, unknown> | null;
      if (!projection || projection.state !== 'projected' || !Array.isArray(projection.occurrences))
        throw new Error('invalid_timing');
      for (const occurrence of projection.occurrences) {
        if (!occurrence || typeof occurrence !== 'object' || Array.isArray(occurrence))
          throw new Error('invalid_occurrence');
        const row = occurrence as Record<string, unknown>;
        if (
          typeof row.id !== 'string' ||
          !row.id ||
          typeof row.instantIso !== 'string' ||
          !Number.isFinite(Date.parse(row.instantIso))
        )
          throw new Error('invalid_occurrence');
        rows.push({
          id: `${id}:${row.id}`,
          clientId: input.clientId,
          contextRevision: input.contextRevision,
          label: 'Prescribed medication',
          href: `/clients/${encodeURIComponent(input.clientId)}/medications`,
          at: row.instantIso,
          outcome: 'scheduled',
        });
      }
    }
  } catch {
    coverage = rows.length ? 'limited' : 'unavailable';
  }
  return { coverage, rows };
}
